/* eslint-disable react-hooks/exhaustive-deps */

import { MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { Stack } from '@mews/std';
import { isIos, isTargetDescendant, WithChildren } from '@optimus-web/common';
import { ClickAwayContext } from './ClickAwayContext';
import { ClickAwayContextValue, ClickAwayOptions } from './types';
import { isClickAwayDisabledInTheComponentTree } from './utils';

/**
 * wait time between next click away logic can be called
 */
const DEBOUNCE_WAIT = 300;

/**
 * timestamp when user clicked last time
 */
let lastClickTimestamp = new Date().getMilliseconds();

/**
 * Singleton stack of handlers
 */
const handlersStack = new Stack<ClickAwayOptions>();

/**
 * This context provider takes care of adding/removing document click listener and holds all registered
 * event handler callbacks.
 */
export const ClickAwayContextProvider: React.FC<WithChildren> = ({ children }) => {
    const previousItemsCountRef = useRef(handlersStack.size);
    const mouseDownTargetElement = useRef<HTMLElement | null>(null);
    const mouseUpTargetElement = useRef<HTMLElement | null>(null);

    const addListener = () => {
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('touchstart', handleMouseDown);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchend', handleMouseDown);
        document.addEventListener('click', handleClick, true);
    };

    const removeListener = () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('touchstart', handleMouseDown);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchend', handleMouseDown);
        document.removeEventListener('click', handleClick, true);
    };

    const handleClick = useCallback((event: Event) => {
        if (
            event.timeStamp - lastClickTimestamp < DEBOUNCE_WAIT ||
            !mouseUpTargetElement.current?.isSameNode(mouseDownTargetElement.current)
        ) {
            // to prevent closing multiple components by clicking multiple times
            return;
        }

        lastClickTimestamp = event.timeStamp;

        const firstOnStack = handlersStack.peek();

        // no handler or no roots
        if (!firstOnStack?.handler || !firstOnStack.roots.length) {
            return;
        }

        // clicked into some element that disables clickAway
        if (isClickAwayDisabledInTheComponentTree(event)) {
            return;
        }

        // This captures closing virtual keyboard on iOS, but not i.e. focusing next field.
        const shouldNotCloseOnIOS = document.activeElement && document.activeElement.tagName === 'BODY';
        if (isIos() && shouldNotCloseOnIOS) {
            return;
        }

        const clickedInRoot = firstOnStack.roots.some((getRoot) => isTargetDescendant(event, getRoot()));

        if (!clickedInRoot) {
            firstOnStack.handler(event as unknown as MouseEvent);
        }
    }, []);

    const handleMouseDown = (e: Event) => {
        mouseDownTargetElement.current = e.target as HTMLElement;
    };

    const handleMouseUp = (e: Event) => {
        mouseUpTargetElement.current = e.target as HTMLElement;
    };

    const registerClickAway = useCallback((options: ClickAwayOptions) => {
        handlersStack.push(options);

        if (previousItemsCountRef.current === 0 && handlersStack.size === 1) {
            addListener();
        }
        previousItemsCountRef.current = handlersStack.size;
    }, []);

    const deregisterClickAway = useCallback(() => {
        handlersStack.pop();

        if (previousItemsCountRef.current === 1 && !handlersStack.size) {
            removeListener();
        }
        previousItemsCountRef.current = handlersStack.size;
    }, []);

    // do some cleanup
    useEffect(() => removeListener, []);

    const contextValue: ClickAwayContextValue = useMemo(
        () => ({ registerClickAway, deregisterClickAway }),
        [registerClickAway, deregisterClickAway]
    );

    return <ClickAwayContext.Provider value={contextValue}>{children}</ClickAwayContext.Provider>;
};
